import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import Wrapper from './InsightsStyle';
import AdditionalResources from '../Shared/AdditionalResources';
import FilesLists from '../Shared/FilesLists';
import FilesTitles from '../Shared/FilesTitles';
import PortalSearchBar from '../Shared/PortalSearchBar';
import LoadingIndicator from './LoadingIndicator';
import Preview from '../Shared/Preview';
import FileEndpoints from '../../Services/FileEndpoints';
import {
    EXTENSION_TO_MIME_TYPE,
    parseFilenameFromContentDisposition,
} from '../../Utility/FileResponseUtility';
import { fetchFolderChildrenRecursive } from '../../Utility/FileFolderTreeUtility';

/** Maps Insights content width (reflects browser zoom via CSS px) to 3–6 columns — no horizontal scroll. */
function computeInsightGridColumns(widthPx) {
    if (!Number.isFinite(widthPx) || widthPx <= 0) return 4;
    let cols = Math.round(widthPx / 268);
    cols = Math.max(3, Math.min(6, cols));
    if (widthPx < 1340) {
        cols = Math.min(cols, 4);
    }
    return cols;
}

const Insights = () => {
    const [folders, setFolders] = useState([]);
    const [searching, setSearching] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [previewItem, setPreviewItem] = useState(false);
    const [loading, setLoading] = useState(true);
    const [firstChildLoaded, setFirstChildLoaded] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);

    const objRef = useRef();
    const contentRef = useRef(null);
    const [gridCols, setGridCols] = useState(4);

    const filterFolders = useMemo(() => (folders) => {
        return folders
            .filter(folder => folder.isFolder)
            .map(folder => folder.children ? { ...folder, children: filterFolders(folder.children) } : folder);
    }, []);

    const fetchFolderChildren = useCallback((folder) => {
        return fetchFolderChildrenRecursive(folder, {
            getFiles: (...args) => FileEndpoints.getFiles(...args),
            setFolders,
            onError: (error) => console.error("Error fetching folder children:", error),
        });
    }, []);

    const loadFolders = useCallback(() => {
        FileEndpoints.getFiles(null, 'GGC Insights', null)
            .then(({ data }) => {
                const newFolders = data.filter(item => item.isFolder).sort((a, b) => a.order - b.order);
                setFolders(newFolders);

                // Load all first-level folders and their children concurrently
                const folderPromises = newFolders.map((folder, index) => {
                    return fetchFolderChildren(folder).then(() => {

                        if (index === 0) {
                            setFirstChildLoaded(true);
                        }
                    });
                });

                return Promise.all(folderPromises);
            })
            .catch(error => {
                console.error("Error loading folders:", error);
            });
    }, [fetchFolderChildren]);

    useEffect(() => {
        loadFolders();
    }, [loadFolders]);

    useEffect(() => {
        if (firstChildLoaded) {
            setLoading(false);
        }
    }, [firstChildLoaded]);

    useLayoutEffect(() => {
        if (loading) return undefined;
        const el = contentRef.current;
        if (!el) return undefined;

        const updateCols = () => {
            const w = el.getBoundingClientRect().width;
            setGridCols(computeInsightGridColumns(w));
        };

        updateCols();
        const ro = new ResizeObserver(updateCols);
        ro.observe(el);
        window.addEventListener('resize', updateCols);
        window.visualViewport?.addEventListener('resize', updateCols);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', updateCols);
            window.visualViewport?.removeEventListener('resize', updateCols);
        };
    }, [loading]);

    const fork = useCallback((id) => {
        const rightElement = document.querySelector('.right');
        const targetElement = document.querySelector(`#${id}`);
        if (rightElement && targetElement) {
            rightElement.scrollTo(0, targetElement.offsetTop - 110);
        }
    }, []);

    const download = useCallback((e, item) => {
        FileEndpoints.download(item.fileId)
            .then((res) => {
                const filename = parseFilenameFromContentDisposition(res.headers['content-disposition']);
                const dataUrl = window.URL.createObjectURL(new Blob([res.data]));
                let alink = document.createElement('a');
                alink.href = dataUrl;
                alink.download = filename;
                alink.click();
            })
            .catch(error => {
                console.error("Error downloading file:", error);
            });
    }, []);

    const checkForMatchingFiles = (folder, searching = "") => {
        if (!folder.isFolder || !folder.children) return false;

        return folder.children.some(item => {
            if (!item.isFolder) {
                const searchLower = searching.toLowerCase();
                return (
                    searching.length === 0 ||
                    item.title.toLowerCase().includes(searchLower) ||
                    item.description.toLowerCase().includes(searchLower)
                );
            }
            return checkForMatchingFiles(item, searching);
        });
    };

    const preview = useCallback((e, item) => {
        if (e.target.classList.contains('download-button')) {
            e.preventDefault();
            return;
        }
        setPreviewItem(item);
        setPreviewLoading(true);
        setShowPreview(true);

        FileEndpoints.download(item.fileId)
            .then((res) => {
                const filename = parseFilenameFromContentDisposition(res.headers['content-disposition']);

                const fileExtension = filename.split('.').pop().toLowerCase();
                const contentType = EXTENSION_TO_MIME_TYPE[fileExtension] || 'application/octet-stream';
                item.url = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
                item.name = filename;
                setPreviewLoading(false);
            })
            .catch(error => {
                console.error("Error fetching preview:", error);
            });
    }, []);

    const renderFolders = useCallback((folders, searching, preview, download) => {
        return folders.map(folder => {

            const hasMatchingFiles = checkForMatchingFiles(folder, searching)
            const hasMatchingFilesInSameFolder = folder.isFolder && folder.children && folder.children.some(item => !item.isFolder && (searching.length === 0 || item.title.toLowerCase().includes(searching) || item.description.toLowerCase().includes(searching)));

            if (hasMatchingFiles) {
                return (
                    <div key={folder.fileId} id={`Id${folder.fileId}`}>
                        {hasMatchingFilesInSameFolder &&
                            <h2>{folder.title}</h2>}
                        <FilesLists folder={folder} searching={searching} preview={preview} download={download} />
                        <div className="children">
                            {renderFolders(folder.children, searching, preview, download)}
                        </div>
                    </div>
                );
            }
            return null;
        }).filter(Boolean);
    }, [searching]);

    return (
        <Wrapper>
            <Preview previewItem={previewItem} showPreview={showPreview} setShowPreview={setShowPreview} setPreviewLoading={setPreviewLoading} previewloading={previewLoading} download={download} objRef={objRef} />
            <div className="left">
                {firstChildLoaded && <FilesTitles folders={filterFolders(folders)} fork={fork} />}
            </div>
            <div className="right">
                {loading ? <LoadingIndicator /> : (
                    <div
                        className="content"
                        ref={contentRef}
                        style={{ '--insights-grid-cols': gridCols }}
                    >
                        {/* <PortalSearchBar
                            value={searching}
                            onChange={(e) => setSearching(e.target.value.toLowerCase())}
                            placeholder="Search insights"
                        /> */}
                        <p>Available for download</p>
                        {folders && renderFolders(folders, searching, preview, download)}
                    </div>
                )}
                {/* <AdditionalResources /> */}
            </div>
        </Wrapper>
    );
};

export default Insights;